import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { researchAPI } from '../services/api/researchApi';
import { LiteratureSource, LiteratureOptions } from '../schemas/chat';
import type { AxiosResponse } from '../lib/api';
import { getErrorKey } from '../lib/errors';

interface LiteratureState {
  sources: LiteratureSource[];
  loading: boolean;
  error: string | null;
  selectedSources: string[];
  options: LiteratureOptions;
}

type LiteratureAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_SOURCES'; payload: LiteratureSource[] }
  | { type: 'TOGGLE_SOURCE'; payload: string }
  | { type: 'SELECT_ALL_SOURCES' }
  | { type: 'CLEAR_SELECTED_SOURCES' }
  | { type: 'SET_SELECTED_SOURCES'; payload: string[] }
  | { type: 'SET_OPTIONS'; payload: LiteratureOptions };

const getDefaultOptions = (): LiteratureOptions => ({
  language: 'chinese',
  year_to: new Date().getFullYear(),
  year_from: new Date().getFullYear() - 1,
  max_results: 5,
  fields_of_study: [],
});

const initialState: LiteratureState = {
  sources: [],
  loading: false,
  error: null,
  selectedSources: [],
  options: getDefaultOptions(),
};

function literatureReducer(state: LiteratureState, action: LiteratureAction): LiteratureState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'SET_SOURCES':
      return { ...state, sources: action.payload, loading: false, error: null };
    case 'TOGGLE_SOURCE':
      const newSelected = [...state.selectedSources];
      const index = newSelected.indexOf(action.payload);
      if (index > -1) {
        newSelected.splice(index, 1);
      } else {
        newSelected.push(action.payload);
      }
      return { ...state, selectedSources: newSelected };
    case 'SELECT_ALL_SOURCES':
      return {
        ...state,
        selectedSources: state.sources.map(s => s.id),
      };
    case 'CLEAR_SELECTED_SOURCES':
      return { ...state, selectedSources: [] };
    case 'SET_SELECTED_SOURCES':
      return { ...state, selectedSources: action.payload };
    case 'SET_OPTIONS':
      return { ...state, options: { ...state.options, ...action.payload } };
    default:
      return state;
  }
}

interface LiteratureContextType {
  state: LiteratureState;
  fetchSources: () => Promise<void>;
  toggleSource: (id: string) => void;
  selectAllSources: () => void;
  clearSelectedSources: () => void;
  setSelectedSources: (ids: string[]) => void;
  setOptions: (options: LiteratureOptions) => void;
  resetOptions: () => void;
  getSelectedSources: () => string[];
  getOptions: () => LiteratureOptions;
}

const LiteratureContext = createContext<LiteratureContextType | undefined>(undefined);

export const useLiterature = () => {
  const context = useContext(LiteratureContext);
  if (!context) {
    throw new Error('useLiterature must be used within a LiteratureProvider');
  }
  return context;
};

interface LiteratureProviderProps {
  children: ReactNode;
}

export const LiteratureProvider: React.FC<LiteratureProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(literatureReducer, initialState);

  const fetchSources = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      const response: AxiosResponse<{ sources: LiteratureSource[] }> =
        await researchAPI.getLiteratureSources();
      const sources = response.data.sources;
      const normalizedSources = Array.isArray(sources) ? sources : [];
      dispatch({ type: 'SET_SOURCES', payload: normalizedSources });
    } catch (error) {
      console.error('Failed to fetch literature sources:', error);
      const errorKey = getErrorKey(error);
      const errorMessage = t(errorKey);

      dispatch({
        type: 'SET_ERROR',
        payload: errorMessage,
      });
      dispatch({ type: 'SET_SOURCES', payload: [] });
    }
  }, [t]);

  const toggleSource = useCallback((id: string) => {
    dispatch({ type: 'TOGGLE_SOURCE', payload: id });
  }, []);

  const selectAllSources = useCallback(() => {
    dispatch({ type: 'SELECT_ALL_SOURCES' });
  }, []);

  const clearSelectedSources = useCallback(() => {
    dispatch({ type: 'CLEAR_SELECTED_SOURCES' });
  }, []);

  const setSelectedSources = useCallback((ids: string[]) => {
    dispatch({ type: 'SET_SELECTED_SOURCES', payload: ids });
  }, []);

  const setOptions = useCallback((options: LiteratureOptions) => {
    dispatch({ type: 'SET_OPTIONS', payload: options });
  }, []);

  const resetOptions = useCallback(() => {
    dispatch({ type: 'SET_OPTIONS', payload: getDefaultOptions() });
  }, []);

  const getSelectedSources = useCallback(() => {
    return state.selectedSources;
  }, [state.selectedSources]);

  const getOptions = useCallback(() => {
    return state.options;
  }, [state.options]);

  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  return (
    <LiteratureContext.Provider
      value={{
        state,
        fetchSources,
        toggleSource,
        selectAllSources,
        clearSelectedSources,
        setSelectedSources,
        setOptions,
        resetOptions,
        getSelectedSources,
        getOptions,
      }}
    >
      {children}
    </LiteratureContext.Provider>
  );
};

export default LiteratureContext;
